import { NgClass } from '@angular/common';
import { Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

import { FileSizePipe } from '../../../sdk/pipes';
import { FileUploaderConfig, UploadFile } from './file-uploader.model';
import { getFileIconFromFormats as getFileIconFromTypes } from './file-uploader.utils';
import { FileUploaderItemComponent } from './file-uploader-item';

const DEFAULT_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const DEFAULT_MAX_LENGTH = 10;
const DEFAULT_MULTIPLE_STATE = true;

@Component({
  selector: 'rms-file-uploader',
  imports: [NgClass, TranslateModule, FileUploaderItemComponent, MatButton],
  templateUrl: './file-uploader.component.html',
  styleUrl: './file-uploader.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
    FileSizePipe,
  ],
})
export class FileUploaderComponent implements ControlValueAccessor {
  @Input() public config?: FileUploaderConfig;
  @Input() public placeholder?: string;
  @Input() public invalid = false;

  private readonly fileSizePipe = inject(FileSizePipe);

  public files: UploadFile[] = [];
  public isDisabled = false;
  public isDragOver = false;

  private onChange?: (files: File[]) => void;
  private onTouched?: () => void;

  public get title(): string {
    if (this.placeholder) {
      return this.placeholder;
    }

    return this.multiple ? 'libs.file-uploader.drop-zone.multiple-title' : 'libs.file-uploader.drop-zone.single-title';
  }

  public get multiple(): boolean {
    return this.config?.multiple ?? DEFAULT_MULTIPLE_STATE;
  }

  public get maxFileSize(): number {
    return this.config?.maxFileSize ?? DEFAULT_FILE_SIZE;
  }

  public get maxLength(): number {
    return this.multiple ? (this.config?.maxLength ?? DEFAULT_MAX_LENGTH) : 1;
  }

  public get alwaysDisplayUploader(): boolean {
    return this.config?.alwaysDisplayUploader ?? false;
  }

  public get descriptionTranslateParams(): { maxLength: number; maxSize: string } {
    return {
      maxLength: this.maxLength,
      maxSize: this.fileSizePipe.transform(this.maxFileSize, 0),
    };
  }

  public get fileIcon(): string | null {
    return getFileIconFromTypes(this.config?.acceptedTypes ?? null);
  }

  public onFileClick(event: Event): void {
    const input = event.target as HTMLInputElement;

    input.value = '';
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }

    this.pushFiles(Array.from(input.files));
  }

  public onDelete(index: number): void {
    this.files = this.files.filter((_, i) => i !== index);

    this.onChange?.(this.files);
  }

  validate(): ValidationErrors | null {
    const invalidSize = this.files.filter((file) => 'error' in file && file.error !== undefined).length > 0;

    return invalidSize ? { invalidSize } : null;
  }

  writeValue(files: File[] | null): void {
    this.files = files || [];
  }

  registerOnChange(fn: (files: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.isDisabled || !event.dataTransfer?.files) {
      return;
    }

    this.pushFiles(Array.from(event.dataTransfer.files));
  }

  private pushFiles(files: UploadFile[]): void {
    if (this.files.length >= this.maxLength) {
      return;
    }

    const availableSlots = this.maxLength - this.files.length;
    const filteredFiles = files
      .filter((file) => {
        if (!this.config?.acceptedTypes?.length) {
          return true;
        }

        return this.config?.acceptedTypes?.includes(file.type);
      })
      .slice(0, availableSlots);

    filteredFiles.forEach((file) => {
      const invalidFileSize = !!this.config?.maxFileSize && file.size > this.config.maxFileSize;

      if (invalidFileSize) {
        file.error = 'libs.file-uploader.errors.size';
      }

      this.files.push(file);
    });

    this.onChange?.(this.files);
    this.onTouched?.();
  }
}
